import { Widget } from "../enum";
import { INode, IRead } from "../@types/device";
import { SourceState } from "../@types/source";
import { ServiceContextType } from "../@types/service";

/**
 * RTDB drops empty arrays and can hand a sparse array back as an object with
 * numeric keys, so anything stored as a list has to come back through here.
 */
export const asArray = <T,>(v: T[] | { [k: string]: T } | null | undefined): T[] => {
  if (!v) return [];
  if (Array.isArray(v)) return v.filter((x) => x != null);
  return Object.keys(v)
    .sort((a, b) => Number(a) - Number(b))
    .map((k) => (v as { [k: string]: T })[k])
    .filter((x) => x != null);
};

/** set() throws on an undefined value, so every write goes through this first. */
export const prune = <T extends object,>(o: T): T => {
  const out: any = {};
  for (const [k, v] of Object.entries(o)) if (v !== undefined) out[k] = v;
  return out as T;
};

export const stripNulls = <T extends object,>(o: T): T => {
  const out: any = {};
  for (const [k, v] of Object.entries(o)) if (v != null) out[k] = v;
  return out as T;
};

/** Returns the widget only if it is a genuine member -- never throws. */
export const widgetOf = (node: INode): Widget | undefined =>
  node?.widget && (Object.values(Widget) as string[]).includes(node.widget)
    ? (node.widget as Widget)
    : undefined;

/**
 * The live state object the node's `addr` points at. Never null.
 * `ctx.state` has an index signature, so the old `(ctx as any)` cast is gone;
 * the `?.` stays, because createContext is seeded with `{} as ServiceContextType`.
 */
const stateOf = (ctx: ServiceContextType, addr?: string): SourceState =>
  (addr && ctx.state?.[addr]) || {};

export const rawValue = (
  ctx: ServiceContextType,
  addr: string | undefined,
  key: string
): number | undefined => {
  const v = stateOf(ctx, addr)[key];
  return typeof v === "number" ? v : undefined;
};

/** Applies the read's scale. Returns undefined when there is no live value. */
export const readValue = (
  ctx: ServiceContextType,
  read: IRead,
  fallbackAddr?: string
): number | undefined => {
  const raw = rawValue(ctx, read.addr ?? fallbackAddr, read.key);
  if (raw === undefined) return undefined;
  return raw / (read.scale || 1);
};

export const rgbKeys = (node: INode): { r: string; g: string; b: string } =>
  node.rgb ?? {
    r: `${node.stateKey}r`,
    g: `${node.stateKey}g`,
    b: `${node.stateKey}b`,
  };

export const sliderCfg = (node: INode): { min: number; max: number; step: number } => ({
  min: node.slider?.min ?? 0,
  max: node.slider?.max ?? 255,
  step: node.slider?.step || 1,
});

export const gaugeCfg = (node: INode): { min: number; max: number; unit?: string } => ({
  min: node.gauge?.min ?? 0,
  max: node.gauge?.max ?? 255,
  unit: node.gauge?.unit,
});

export const clamp = (v: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, v));

/** Position of `v` within [min,max] as a 0..100 percentage. */
export const pct = (v: number | undefined, min: number, max: number): number => {
  if (v === undefined || max === min) return 0;
  return clamp(((v - min) / (max - min)) * 100, 0, 100);
};

/**
 * Every (addr, key) this node WRITES.
 *
 * Reads are deliberately absent: `reads[]`, `ac.indoor`, `ac.outdoor` and
 * `ac.runningKey` all pull FROM a tree, and pulling a sensor value into a
 * device on another source is the intended design -- a lamp on /nodes whose
 * readout shows suhu1 from /data. Guarding those per-field would break the AC
 * widget, which hardcodes addr:"data" for its indoor and outdoor sensors.
 *
 * `inputs[]` is enumerated independently of the widget, which is what closes
 * the hole the widget-type check alone cannot: a READOUT with extra inputs
 * still writes.
 */
export const writeTargets = (n: INode): { addr: string; key: string }[] => {
  const at = (key: string, addr: string = n.addr) => ({ addr, key });
  const out: { addr: string; key: string }[] = [];

  if (n.widget === Widget.SLIDER || n.widget === Widget.BUTTONS)
    out.push(at(n.stateKey));

  if (n.widget === Widget.COLOR) {
    const k = rgbKeys(n);
    out.push(at(k.r), at(k.g), at(k.b));
  }

  if (n.widget === Widget.AC)
    [n.ac?.targetKey, n.ac?.fanKey, n.ac?.swingKey].forEach(
      (k) => k && out.push(at(k))
    );

  asArray(n.inputs).forEach((i) => i.key && out.push(at(i.key, i.addr ?? n.addr)));

  return out.filter((t) => t.key);
};
